import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { X, ChevronRight, Plus, Move, CheckCircle2, Sparkles } from 'lucide-react';

const steps = [
  {
    id: 'welcome',
    target: null,
    title: "Let's get you Brioritized!",
    content: "I'll guide you through adding your first interest and show you how to use the grid effectively.",
    position: 'center',
    showSkip: true
  },
  {
    id: 'add-button',
    target: '[data-testid="add-interest-btn"]',
    title: "Add an Interest",
    content: "Click here to add something that matters to you — a goal, concern, task, or anything affecting your wellbeing.",
    position: 'bottom',
    highlightPadding: 20
  },
  {
    id: 'quadrant-tl',
    target: '[data-testid="q1-label"]',
    title: "High Attention / High Impact",
    content: "Your focus zone. Place interests here that need your attention AND where your actions can make a real difference.",
    position: 'bottom',
    highlightPadding: 60
  },
  {
    id: 'quadrant-tr',
    target: '[data-testid="q2-label"]',
    title: "High Attention / Low Impact",
    content: "Caution zone. If you're spending energy here, ask yourself: is this actually within my control? Consider if it's worth the stress.",
    position: 'bottom',
    highlightPadding: 60
  },
  {
    id: 'quadrant-bl',
    target: '[data-testid="q3-label"]',
    title: "Low Attention / High Impact",
    content: "Opportunity zone. These are things you CAN influence but aren't focusing on. They might deserve more of your attention.",
    position: 'top',
    highlightPadding: 60
  },
  {
    id: 'quadrant-br',
    target: '[data-testid="q4-label"]',
    title: "Low Attention / Low Impact",
    content: "Release zone. These aren't in your control and you're not focused on them. Consider letting go or accepting until you can focus more on them.",
    position: 'top',
    highlightPadding: 60
  },
  {
    id: 'drag-tip',
    target: null,
    title: "Drag to Reposition",
    content: "As circumstances change, drag your interests around the grid. An urgent deadline might move something up. Realizing you can't control something? Move it right.",
    position: 'center',
    icon: Move
  },
  {
    id: 'complete',
    target: null,
    title: "You're Ready!",
    content: "Start adding your interests and watch your priorities become clearer. Focus on what you can control, release what you can't, and take care of your wellbeing.",
    position: 'center',
    icon: Sparkles,
    isFinal: true
  }
];

export const InteractiveWalkthrough = ({ isActive, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = steps[currentStep];

  const updateTargetRect = useCallback(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step.target]);

  useEffect(() => {
    if (isActive) {
      updateTargetRect();
      window.addEventListener('resize', updateTargetRect);
      window.addEventListener('scroll', updateTargetRect);
      return () => {
        window.removeEventListener('resize', updateTargetRect);
        window.removeEventListener('scroll', updateTargetRect);
      };
    }
  }, [isActive, currentStep, updateTargetRect]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.setItem('walkthrough_completed', 'true');
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem('walkthrough_completed', 'true');
    onSkip?.();
  };

  const getTooltipPosition = () => {
    const tooltipWidth = 320;
    const tooltipHeight = 280; // Increased to account for full content
    const padding = 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16;

    // For center position or no target, center in viewport with bounds checking
    if (!targetRect || step.position === 'center') {
      const centerTop = Math.max(margin, (viewportHeight - tooltipHeight) / 2);
      const centerLeft = Math.max(margin, (viewportWidth - tooltipWidth) / 2);
      
      return {
        top: `${centerTop}px`,
        left: `${centerLeft}px`,
        transform: 'none'
      };
    }

    let top, left;

    // Calculate initial position based on preferred position
    switch (step.position) {
      case 'bottom':
        top = targetRect.bottom + padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + padding;
        break;
      default:
        top = (viewportHeight - tooltipHeight) / 2;
        left = (viewportWidth - tooltipWidth) / 2;
    }

    // Clamp to viewport bounds with margin
    if (left < margin) {
      left = margin;
    } else if (left + tooltipWidth > viewportWidth - margin) {
      left = viewportWidth - tooltipWidth - margin;
    }

    if (top < margin) {
      top = margin;
    } else if (top + tooltipHeight > viewportHeight - margin) {
      top = viewportHeight - tooltipHeight - margin;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    };
  };

  if (!isActive) return null;

  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={handleSkip} />
      
      {/* Spotlight on target */}
      {targetRect && (
        <div
          className="absolute bg-transparent pointer-events-none"
          style={{
            top: targetRect.top - (step.highlightPadding || 10),
            left: targetRect.left - (step.highlightPadding || 10),
            width: targetRect.width + (step.highlightPadding || 10) * 2,
            height: targetRect.height + (step.highlightPadding || 10) * 2,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            borderRadius: '12px'
          }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          className="absolute w-80 max-h-[calc(100vh-32px)] bg-background rounded-xl shadow-2xl border border-border pointer-events-auto overflow-hidden flex flex-col"
          style={getTooltipPosition()}
        >
          {/* Progress */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-muted rounded-t-xl overflow-hidden z-10">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          <div className="p-5 pt-6 overflow-y-auto flex-1">
            {/* Icon for center modals */}
            {Icon && (
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-3 mx-auto">
                <Icon className="w-6 h-6 text-white" />
              </div>
            )}

            <h3 className="font-bold text-lg mb-2 text-center">{step.title}</h3>
            <p className="text-sm text-muted-foreground text-center leading-relaxed mb-4">
              {step.content}
            </p>

            {/* Step counter */}
            <div className="flex justify-center gap-1.5 mb-4">
              {steps.map((_, idx) => (
                <div
                  key={`step-${idx}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'w-4 bg-emerald-500' 
                      : idx < currentStep 
                        ? 'w-1.5 bg-emerald-500/50' 
                        : 'w-1.5 bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              {step.showSkip ? (
                <Button variant="ghost" size="sm" onClick={handleSkip}>
                  Skip tour
                </Button>
              ) : (
                <div />
              )}
              
              <Button 
                size="sm" 
                onClick={handleNext}
                className="gap-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90"
              >
                {step.isFinal ? (
                  <>
                    Start Brioritizing
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {currentStep === 0 ? "Let's go" : "Next"}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
